/****** UniForm.jsx ******/

import { useEffect, useRef, useState } from "react";
import html2pdf from "html2pdf.js";
import "./UniForm.css";

export default function UniForm() {
  const canvasRef = useRef(null);
  const pdfRef = useRef(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [signature, setSignature] = useState("");
  const [form, setForm] = useState({
    nom: "",
    prenom: "",
    fonction: "",
  });

  function getTodayDate() {
    return new Date().toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  function handleChange(e) {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function getPointerPosition(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const event = e.touches ? e.touches[0] : e;

    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  }

  function startDrawing(e) {
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPointerPosition(e, canvas);

    setIsDrawing(true);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  }

  function draw(e) {
    if (!isDrawing) return;

    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPointerPosition(e, canvas);

    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  }

  function stopDrawing() {
    if (!isDrawing) return;

    const canvas = canvasRef.current;

    setIsDrawing(false);
    setSignature(canvas.toDataURL("image/png"));
  }

  function clearSignature() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature("");
  }

async function downloadPDF() {
  if (!form.nom.trim() || !form.prenom.trim() || !form.fonction.trim()) {
    alert("Merci de remplir tous les champs avant de télécharger le PDF.");
    return;
  }

  if (!signature) {
    alert("Merci de signer le document avant de télécharger le PDF.");
    return;
  }

  const element = pdfRef.current;

  element.classList.add("pdf-mode");

  const options = {
    margin: [12, 12, 12, 12],
    filename: "consentement-unibot.pdf",
    image: {
      type: "jpeg",
      quality: 0.98,
    },
    html2canvas: {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: 0,
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    },
    pagebreak: {
      mode: ["css", "legacy"],
      avoid: [
        ".doc-section",
        ".sign-area",
        ".signature-box",
        ".doc-title",
        ".doc-subtitle",
        "h1",
        "h2",
        "p",
      ],
    },
  };

  await html2pdf().set(options).from(element).save();

  element.classList.remove("pdf-mode");
}

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    function resizeCanvas() {
      const rect = canvas.getBoundingClientRect();
      const oldSignature = canvas.toDataURL("image/png");

      canvas.width = rect.width;
      canvas.height = 220;

      ctx.lineWidth = 2.5;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#1f2937";

      if (signature) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        };
        img.src = oldSignature;
      }
    }

    resizeCanvas();

    window.addEventListener("resize", resizeCanvas);

    return () => window.removeEventListener("resize", resizeCanvas);
  }, [signature]);

  return (
    <div className="uniFormPage">
      <div className="page">
        <div className="topbar">
          <div className="brand-badge">
            <span className="brand-dot" />
            <span>Projet UNIBOT : formulaire de consentement</span>
          </div>
        </div>

        <div className="layout">
          <section className="card document-wrap">
            <article className="document" ref={pdfRef}>
              <h1 className="doc-title">
                DOCUMENT D’INFORMATION ET DE CONSENTEMENT
              </h1>

              <div className="doc-rule" />

              <p className="doc-subtitle">
                Projet d’utilisation de contenus pédagogiques pour l’entraînement
                d’un modèle de langage pédagogique (LLM)
              </p>

              <section className="doc-section">
                <h2>1. Présentation du projet</h2>
                <p>
                  Dans le cadre d’un projet de recherche universitaire UNIBOT,
                  Nîmes Université développe et entraîne un modèle de langage
                  spécialisé dans les tâches pédagogiques de l’enseignement
                  supérieur destiné à accompagner les étudiants dans leur
                  apprentissage.
                </p>
                <p>
                  Ce modèle a pour objectif de répondre à des questions
                  techniques, proposer des explications pédagogiques, générer des
                  supports de révision et simuler des exercices pratiques.
                </p>
                <p>
                  Afin d’assurer la qualité scientifique, le modèle sera entraîné
                  à partir de supports de cours fournis par les enseignants.
                </p>
              </section>

              <section className="doc-section">
                <h2>2. Nature des données utilisées</h2>
                <p>
                  Supports de cours, documents pédagogiques et contenus
                  méthodologiques.
                </p>
                <p>
                  Les étudiants n’auront aucun accès direct aux documents
                  originaux.
                </p>
              </section>

              <section className="doc-section">
                <h2>3. Finalités</h2>
                <p>
                  Utilisation exclusivement à des fins de recherche,
                  d’amélioration pédagogique et d’usage interne dans le cadre du
                  projet UNIBOT.
                </p>
                <p>Aucune utilisation commerciale sans accord préalable.</p>
              </section>

              <section className="doc-section">
                <h2>4. Modalités d’utilisation</h2>
                <p>
                  Les contenus seront sécurisés, transformés et utilisés pour
                  entraîner le modèle.
                </p>
                <p>
                  Aucune reproduction directe ne sera accessible aux utilisateurs.
                </p>
              </section>

              <section className="doc-section">
                <h2>5. Droits d’auteur</h2>
                <p>Les enseignants conservent leurs droits.</p>
                <p>Aucune cession de droits n’est effectuée.</p>
                <p>Les contenus ne seront pas diffusés.</p>
              </section>

              <section className="doc-section">
                <h2>6. Confidentialité</h2>
                <p>
                  Stockage sécurisé, accès restreint et absence de diffusion des
                  contenus bruts.
                </p>
                <p>Aucune donnée personnelle sensible ne doit être incluse.</p>
              </section>

              <section className="doc-section">
                <h2>7. Absence d’accès direct</h2>
                <p>Les étudiants accèdent uniquement à des réponses générées.</p>
                <p>Le système ne remplace pas l’enseignement.</p>
              </section>

              <section className="doc-section">
                <h2>8. Droit de retrait</h2>
                <p>
                  Possibilité de retrait à tout moment avec suppression des
                  données.
                </p>
              </section>

              <section className="doc-section consent-section">
                <h2>9. Consentement</h2>

                <p>
                  Je soussigné(e),{" "}
                  <span className="fillable">{form.nom || "Nom"}</span>{" "}
                  <span className="fillable">{form.prenom || "Prénom"}</span>,
                  exerçant la fonction de{" "}
                  <span className="fillable">
                    {form.fonction || "Fonction"}
                  </span>
                  , reconnais avoir pris connaissance des informations ci-dessus
                  et autorise l’utilisation des contenus pédagogiques concernés
                  dans le cadre du projet UNIBOT.
                </p>

                <p>
                  Fait le{" "}
                  <span className="fillable">{getTodayDate()}</span>.
                </p>
              </section>

              <section className="sign-area">
                <div className="sign-grid">
                  <div />
                  <div>
                    <strong>Signature</strong>

                    <div className="signature-box">
                      {signature && <img src={signature} alt="Signature" />}
                    </div>
                  </div>
                </div>
              </section>
            </article>
          </section>

          <aside className="card panel">
            <h3>Compléter et signer</h3>

            <form className="form-grid">
              <div className="field">
                <label htmlFor="nom">Nom</label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={form.nom}
                  onChange={handleChange}
                  placeholder="Votre nom"
                  required
                />
              </div>

              <div className="field">
                <label htmlFor="prenom">Prénom</label>
                <input
                  type="text"
                  id="prenom"
                  name="prenom"
                  value={form.prenom}
                  onChange={handleChange}
                  placeholder="Votre prénom"
                  required
                />
              </div>

              <div className="field">
                <label htmlFor="fonction">Fonction</label>
                <input
                  type="text"
                  id="fonction"
                  name="fonction"
                  value={form.fonction}
                  onChange={handleChange}
                  placeholder="Ex. Enseignant-chercheur"
                  required
                />
              </div>

              <div className="field">
                <label htmlFor="signaturePad">Signature</label>

                <div className="signature-pad-wrap">
                  <div className="signature-pad-toolbar">
                    <span>Signez directement dans la zone ci-dessous</span>

                    <button
                      className="btn-secondary"
                      type="button"
                      onClick={clearSignature}
                      style={{ padding: "8px 12px", fontSize: 13 }}
                    >
                      Effacer
                    </button>
                  </div>

                  <canvas
                    id="signaturePad"
                    ref={canvasRef}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
              </div>
            </form>

            <div className="download-wrap">
              <button
                className="btn-primary download-btn"
                type="button"
                onClick={downloadPDF}
              >
                Télécharger le PDF
              </button>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}